import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { MapPin, Plus, Search } from "lucide-react";
import { COUNTRIES, MX_STATES, sedeInputSchema, type Country, type Sede } from "@nh/shared";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Badge, Button, Input, Select } from "@/shared/ui/core";
import { EmptyState, Modal, SkeletonTable } from "@/shared/ui/ui";
import {
  AdminGuard,
  DataTable,
  DataTableFrame,
  FilterBar,
  Pagination,
  Td,
  Th,
  Tr,
  DEFAULT_PER_PAGE,
} from "@/features/admin/components";
import { useCreateSede, useSedes, useUpdateSede } from "./sedes.queries";
import { PlaceSearch } from "@/features/panel/address/PlaceSearch";

const COUNTRY_LABELS: Record<string, string> = {
  MX: "México",
  US: "Estados Unidos",
  OTHER: "Otro",
};

/**
 * ═══════════════════════════════════════════════════════════════════
 * Sedes — réplica de `shop/admin/manage-sedes` de producción (Anahí,
 * 27-ago). Las ubicaciones físicas de la marca, listas DESDE YA para
 * que cuando entre la integración de envíos (Trasso) cada guía tenga
 * de dónde salir.
 *
 * El alta va en un MODAL (Anahí, 27-ago: "más minimalista y sencillo")
 * — la pantalla es el directorio, y "Agregar sede" abre el formulario
 * encima. Editar abre el mismo modal con la fila cargada. Una sede no
 * se borra: se APAGA cuando deja de operar.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminSedesPage() {
  return (
    <AdminGuard>
      <SedesScreen />
    </AdminGuard>
  );
}

const EMPTY_FORM = {
  name: "",
  country: "MX" as Country,
  city: "",
  address: "",
  state: "",
  zip: "",
  phone: "",
};


function SedesScreen() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  /*
   * Las filas por página las elige quien mira (Anahí, 29-ago): 5, 10 o 20,
   * con CINCO por defecto. Vive en el estado y no como constante porque
   * ahora cambia en caliente; al cambiarlo se vuelve a la página 1, que es
   * lo único sensato — la página 4 de 5 en 5 no es la página 4 de 20 en 20.
   */
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  // Filtros y paginación de SERVIDOR: solo viaja la página visible.
  const sedes = useSedes({
    page,
    perPage: perPage,
    ...(search.trim() ? { query: search.trim() } : {}),
    ...(status ? { status: status as "active" | "inactive" } : {}),
  });
  const crear = useCreateSede();
  const actualizar = useUpdateSede();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  /** id de la sede en edición; `null` = el modal es un alta. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  /*
   * ── Los campos de dirección se REVELAN (Anahí, 29-ago) ─────────────
   * "Aquí solo deben estar estos campos y el teléfono, y agregando la
   * dirección debe aparecer [...] como en /socio/perfil."
   *
   * Mismo patrón que `AddressSection`: el buscador manda y los campos
   * sueltos aparecen cuando ya hay ALGO QUE CONFIRMAR. Un formulario de
   * seis campos vacíos junto a un buscador que los llena es pedir dos
   * veces lo mismo; enseñarlos ya rellenos los convierte en lo que de
   * verdad son aquí, una confirmación.
   *
   * Siguen editables —Google falla con el número exterior más de lo que
   * parece— y al editar una sede que ya existe salen abiertos, porque
   * ahí sí hay algo que revisar desde el primer momento.
   */
  const [mostrarCampos, setMostrarCampos] = useState(false);

  function set<K extends keyof typeof EMPTY_FORM>(key: K, value: (typeof EMPTY_FORM)[K]) {
    setForm((previous) => ({ ...previous, [key]: value }));
  }

  function abrirAlta() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setMostrarCampos(false);
    setOpen(true);
  }

  function abrirEdicion(sede: Sede) {
    setEditingId(sede.id);
    setForm({
      name: sede.name,
      country: sede.country,
      city: sede.city,
      address: sede.address,
      state: sede.state ?? "",
      zip: sede.zip ?? "",
      phone: sede.phone ?? "",
    });
    setFormError(null);
    // Editando SÍ salen abiertos: ahí hay algo que revisar desde el
    // primer momento, no una dirección por capturar.
    setMostrarCampos(true);
    setOpen(true);
  }

  async function guardar(event: FormEvent) {
    event.preventDefault();
    setFormError(null);

    const parsed = sedeInputSchema.safeParse({
      name: form.name,
      country: form.country,
      city: form.city,
      address: form.address,
      ...(form.state.trim() ? { state: form.state.trim() } : {}),
      ...(form.zip.trim() ? { zip: form.zip.trim() } : {}),
      ...(form.phone ? { phone: form.phone } : {}),
      active: true,
    });
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? t("Revisa los datos de la sede"));
      return;
    }

    try {
      if (editingId) {
        const { active: _active, ...update } = parsed.data;
        await actualizar.mutateAsync({ id: editingId, update });
      } else {
        await crear.mutateAsync(parsed.data);
      }
      setOpen(false);
    } catch (cause) {
      setFormError(
        cause instanceof Error && cause.message
          ? cause.message
          : t("No se pudo guardar la sede"),
      );
    }
  }

  const items = sedes.data?.items ?? [];
  const total = sedes.data?.total ?? 0;

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Sedes")}
        description={t("Las ubicaciones de la marca: el origen de los envíos cuando conectemos la paquetería.")}
      />

      <DataTableFrame aria-busy={sedes.isFetching}>
        {/* El alta vive DENTRO de la tabla, arriba a la derecha (Anahí). */}
        <FilterBar
          actions={
            <Button leftIcon={<Plus />} onClick={abrirAlta}>
              {t("Agregar sede")}
            </Button>
          }
        >
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Buscar")}
            <div className="relative w-56">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ds-ink-3" />
              <Input
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                placeholder={t("Nombre o ciudad…")}
                className="pl-10"
              />
            </div>
          </label>
          <label className="grid gap-1 text-[12px] font-medium text-ds-ink-3">
            {t("Estado")}
            <div className="w-40">
              <Select
                aria-label={t("Estado")}
                value={status}
                onChange={(event) => {
                  setStatus(event.target.value);
                  setPage(1);
                }}
                placeholder={t("Todas")}
                options={[
                  { value: "active", label: t("Activas") },
                  { value: "inactive", label: t("Apagadas") },
                ]}
              />
            </div>
          </label>
        </FilterBar>
        {sedes.isPending ? (
          <div className="p-5">
            <SkeletonTable rows={4} columns={6} />
          </div>
        ) : items.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={<MapPin className="h-5 w-5 text-ds-ink-3" />}
              title={t("Todavía no hay sedes")}
              description={t("Da de alta la primera: será el origen de tus envíos.")}
              action={
                <Button size="sm" leftIcon={<Plus />} onClick={abrirAlta}>
                  {t("Agregar sede")}
                </Button>
              }
            />
          </div>
        ) : (
          <DataTable caption={t("Sedes registradas")}>
            <thead>
              <tr>
                <Th>{t("Nombre")}</Th>
                <Th>{t("Ciudad")}</Th>
                <Th>{t("País")}</Th>
                <Th>{t("Dirección")}</Th>
                <Th>{t("Teléfono")}</Th>
                <Th>{t("Estado")}</Th>
                <Th align="right">{t("Acciones")}</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((sede) => (
                <Tr key={sede.id}>
                  <Td className="font-medium">{sede.name}</Td>
                  <Td>{sede.city}</Td>
                  <Td>{COUNTRY_LABELS[sede.country] ?? sede.country}</Td>
                  <Td className="max-w-[320px] truncate text-ds-ink-3">{sede.address}</Td>
                  <Td className="text-ds-ink-3">{sede.phone ?? "—"}</Td>
                  <Td>
                    <Badge variant={sede.active ? "success" : "neutral"} size="sm" dot>
                      {sede.active ? t("Activa") : t("Apagada")}
                    </Badge>
                  </Td>
                  <Td align="right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => abrirEdicion(sede)}>
                        {t("Editar")}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          actualizar.mutate({ id: sede.id, update: { active: !sede.active } })
                        }
                      >
                        {sede.active ? t("Apagar") : t("Encender")}
                      </Button>
                    </div>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </DataTable>
        )}

        {total > 0 && (
          <Pagination page={page} perPage={perPage} total={total} onPageChange={setPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
        )}
      </DataTableFrame>

      {/* ── El formulario, encima de todo y solo cuando hace falta ────── */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={editingId ? t("Editar sede") : t("Nueva sede")}
      >
        <form onSubmit={(event) => void guardar(event)} className="grid gap-4">
          {/*
            ── UN solo input para la dirección (Anahí, 29-ago) ──────────
            "Aquí en direcciones agrega también la api de google, que solo
            sea un input para agregar la dirección, debe permitir agregar
            dirección en México y Estados Unidos."

            Se busca arriba y los campos de abajo se rellenan solos —país
            incluido, que baja a "Estados Unidos" cuando la dirección lo
            es. Los campos SIGUEN ahí y siguen siendo editables: Google se
            equivoca con el número exterior más veces de las que parece, y
            una sede mal capturada es el origen de TODAS sus guías de
            envío. El buscador ahorra el tecleo, no sustituye la revisión.

            Si el servidor no tiene clave, `PlaceSearch` se esconde solo y
            queda el formulario de siempre.
          */}
          <PlaceSearch
            label={t("Buscar la dirección")}
            onPick={(encontrada) => {
              setForm((actual) => ({
                ...actual,
                ...(encontrada.street ? { address: encontrada.street } : {}),
                ...(encontrada.city ? { city: encontrada.city } : {}),
                ...(encontrada.state ? { state: encontrada.state } : {}),
                ...(encontrada.zip ? { zip: encontrada.zip } : {}),
                // Solo si es un país que la sede admite: Google puede
                // devolver uno que no está en la lista y dejar el
                // selector en un valor imposible de guardar.
                ...(encontrada.country && (COUNTRIES as readonly string[]).includes(encontrada.country)
                  ? { country: encontrada.country as Country }
                  : {}),
              }));
              setMostrarCampos(true);
            }}
            onUnavailable={() => setMostrarCampos(true)}
          />

          {!mostrarCampos && (
            <button
              type="button"
              onClick={() => setMostrarCampos(true)}
              className="w-fit text-[12.5px] font-medium text-ds-violet-pop hover:underline"
            >
              {t("Escribirla a mano")}
            </button>
          )}

          <Input
            label={t("Nombre")}
            value={form.name}
            onChange={(event) => set("name", event.target.value)}
            placeholder={t("Sede CDMX Centro")}
          />
          {/* Los campos de la dirección: aparecen ya rellenos, como
              confirmación de lo que se eligió arriba. Ver `mostrarCampos`. */}
          {mostrarCampos && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <Select
                  label={t("País")}
                  value={form.country}
                  onChange={(event) => set("country", event.target.value as Country)}
                  options={COUNTRIES.map((code) => ({
                    value: code,
                    label: COUNTRY_LABELS[code] ?? code,
                  }))}
                />
                <Input
                  label={t("Ciudad")}
                  value={form.city}
                  onChange={(event) => set("city", event.target.value)}
                  placeholder={t("Ciudad de México")}
                />
              </div>
              <Input
                label={t("Dirección")}
                value={form.address}
                onChange={(event) => set("address", event.target.value)}
                placeholder={t("Calle, número y colonia")}
                hint={t("Completa: será el origen de las guías de envío.")}
              />
              {/* Estado y CP POR SEPARADO: la cotización de Trasso los exige
                  así, y con ellos la sede prellena el origen de cada guía. */}
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label={t("Estado")}
                  value={form.state}
                  onChange={(event) => set("state", event.target.value)}
                  placeholder={t("Ciudad de México")}
                  list="sede-estados"
                />
                <Input
                  label={t("Código postal")}
                  value={form.zip}
                  onChange={(event) => set("zip", event.target.value)}
                  placeholder="06600"
                  inputMode="numeric"
                  maxLength={5}
                />
              </div>
              <datalist id="sede-estados">
                {MX_STATES.map((estado) => (
                  <option key={estado} value={estado} />
                ))}
              </datalist>
            </>
          )}
          <Input
            label={t("Teléfono (opcional)")}
            value={form.phone}
            onChange={(event) => set("phone", event.target.value)}
            placeholder="+52 55 1234 5678"
          />

          {formError && (
            <Alert variant="error" onDismiss={() => setFormError(null)}>
              {formError}
            </Alert>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" type="button" onClick={() => setOpen(false)}>
              {t("Cancelar")}
            </Button>
            <Button type="submit" loading={crear.isPending || actualizar.isPending}>
              {editingId ? t("Guardar cambios") : t("Agregar sede")}
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
